/**
 * Admin Configuration Service
 * Handles API communication for platform content management
 */

import { config } from '../config/environment'

// API base URL configuration
const getApiBaseUrl = (): string => {
  return config.API_BASE_URL
}

// Type definitions
export interface QuickTip {
  id: number
  title: string
  description: string
  url?: string
  category: string
  order_index: number
  tags?: string[]
  active: boolean
  created_at: number
  updated_at: number
}

export interface Template {
  id: number
  name: string
  description: string
  category: string
  file_name: string
  s3_key: string
  s3_url: string
  file_type: string
  file_size: number
  download_count: number
  tags?: string[]
  active: boolean
  created_at: number
  updated_at: number
}

export interface VideoGuide {
  id: number
  title: string
  description: string
  url: string
  category: string
  duration?: string
  thumbnail_url?: string
  order_index: number
  view_count: number
  tags?: string[]
  active: boolean
  created_at: number
  updated_at: number
}

// Input types for creating/updating
export type CreateQuickTipInput = Omit<
  QuickTip,
  'id' | 'created_at' | 'updated_at'
>

export type UpdateQuickTipInput = Partial<
  Omit<QuickTip, 'id' | 'created_at' | 'updated_at'>
>

export type CreateTemplateInput = {
  name: string
  description: string
  category: string
  tags?: string[]
  file: File
}

export type UpdateTemplateInput = Partial<
  Pick<Template, 'name' | 'description' | 'category' | 'active'>
> & {
  tags?: string[]
}

export type CreateVideoGuideInput = Omit<
  VideoGuide,
  'id' | 'created_at' | 'updated_at' | 'view_count'
>

export type UpdateVideoGuideInput = Partial<
  Omit<VideoGuide, 'id' | 'created_at' | 'updated_at' | 'view_count'>
> & {
  tags?: string[]
}

// API Response types
interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

class AdminConfigurationService {
  private baseUrl: string

  constructor() {
    this.baseUrl = getApiBaseUrl()
  }

  private apiFetch(url: string, init?: RequestInit): Promise<Response> {
    return fetch(url, { credentials: 'include', ...init })
  }

  // ===================================================================
  // Quick Tips Management
  // ===================================================================

  async getQuickTips(): Promise<QuickTip[]> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/quick-tips`)
      const result: ApiResponse<QuickTip[]> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch quick tips')
      }

      return result.data || []
    } catch (error) {
      console.error('Error fetching quick tips:', error)
      throw error
    }
  }

  async createQuickTip(tipData: CreateQuickTipInput): Promise<{id: number}> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/quick-tips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tipData)
      })

      const result: ApiResponse<{id: number}> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to create quick tip')
      }

      return result.data!
    } catch (error) {
      console.error('Error creating quick tip:', error)
      throw error
    }
  }

  async updateQuickTip(
    id: number,
    tipData: UpdateQuickTipInput
  ): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/quick-tips/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(tipData)
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to update quick tip')
      }
    } catch (error) {
      console.error('Error updating quick tip:', error)
      throw error
    }
  }

  async deleteQuickTip(id: number): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/quick-tips/${id}`,
        {
          method: 'DELETE'
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete quick tip')
      }
    } catch (error) {
      console.error('Error deleting quick tip:', error)
      throw error
    }
  }

  // ===================================================================
  // Templates Management
  // ===================================================================

  async getTemplates(): Promise<Template[]> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/templates`)
      const result: ApiResponse<Template[]> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch templates')
      }

      return result.data || []
    } catch (error) {
      console.error('Error fetching templates:', error)
      throw error
    }
  }

  async createTemplate(
    templateData: CreateTemplateInput
  ): Promise<{id: number; name: string; fileName: string}> {
    try {
      const formData = new FormData()
      formData.append('file', templateData.file)
      formData.append('name', templateData.name)
      formData.append('description', templateData.description)
      formData.append('category', templateData.category)

      if (templateData.tags && templateData.tags.length > 0) {
        formData.append('tags', JSON.stringify(templateData.tags))
      }

      const response = await this.apiFetch(`${this.baseUrl}/api/admin/templates`, {
        method: 'POST',
        body: formData
      })

      const result: ApiResponse<{id: number; name: string; fileName: string}> =
        await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to create template')
      }

      return result.data!
    } catch (error) {
      console.error('Error creating template:', error)
      throw error
    }
  }

  async updateTemplate(
    id: number,
    templateData: UpdateTemplateInput
  ): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/templates/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(templateData)
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to update template')
      }
    } catch (error) {
      console.error('Error updating template:', error)
      throw error
    }
  }

  async deleteTemplate(id: number): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/templates/${id}`,
        {
          method: 'DELETE'
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete template')
      }
    } catch (error) {
      console.error('Error deleting template:', error)
      throw error
    }
  }

  async generateTemplateDownloadUrl(
    id: number
  ): Promise<{downloadUrl: string; fileName: string; expiresIn: number}> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/templates/${id}/download`,
        {
          method: 'POST'
        }
      )

      const result: ApiResponse<{
        downloadUrl: string
        fileName: string
        expiresIn: number
      }> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate download URL')
      }

      return result.data!
    } catch (error) {
      console.error('Error generating template download URL:', error)
      throw error
    }
  }

  // ===================================================================
  // Video Guides Management
  // ===================================================================

  async getVideoGuides(): Promise<VideoGuide[]> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/video-guides`)
      const result: ApiResponse<VideoGuide[]> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch video guides')
      }

      return result.data || []
    } catch (error) {
      console.error('Error fetching video guides:', error)
      throw error
    }
  }

  async createVideoGuide(
    videoData: CreateVideoGuideInput
  ): Promise<{id: number}> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/video-guides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(videoData)
      })

      const result: ApiResponse<{id: number}> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to create video guide')
      }

      return result.data!
    } catch (error) {
      console.error('Error creating video guide:', error)
      throw error
    }
  }

  async updateVideoGuide(
    id: number,
    videoData: UpdateVideoGuideInput
  ): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/video-guides/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(videoData)
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to update video guide')
      }
    } catch (error) {
      console.error('Error updating video guide:', error)
      throw error
    }
  }

  async deleteVideoGuide(id: number): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/video-guides/${id}`,
        {
          method: 'DELETE'
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete video guide')
      }
    } catch (error) {
      console.error('Error deleting video guide:', error)
      throw error
    }
  }

  async incrementVideoViewCount(id: number): Promise<void> {
    try {
      const response = await this.apiFetch(
        `${this.baseUrl}/api/admin/video-guides/${id}/view`,
        {
          method: 'POST'
        }
      )

      const result: ApiResponse<void> = await response.json()

      if (!result.success) {
        throw new Error(result.error || 'Failed to increment view count')
      }
    } catch (error) {
      console.error('Error incrementing video view count:', error)
      throw error
    }
  }

  // ===================================================================
  // Utility Methods
  // ===================================================================

  async healthCheck(): Promise<{
    success: boolean
    message: string
    timestamp: string
  }> {
    try {
      const response = await this.apiFetch(`${this.baseUrl}/api/admin/health`)
      const result = await response.json()
      return result
    } catch (error) {
      console.error('Health check failed:', error)
      throw error
    }
  }

  // Helper method for file downloads
  async downloadTemplate(id: number): Promise<void> {
    try {
      const {downloadUrl, fileName} = await this.generateTemplateDownloadUrl(id)

      // Create a temporary link and trigger download
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading template:', error)
      throw error
    }
  }

  // Helper method for parsing tags — accepts array (PostgreSQL JSONB) or JSON string (legacy SQLite)
  parseTags(tags?: string[] | string): string[] {
    if (!tags) return []
    if (Array.isArray(tags)) return tags
    try {
      return JSON.parse(tags)
    } catch {
      return []
    }
  }

  // Helper method for formatting file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Helper method for formatting timestamps
  formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString()
  }
}

// Export singleton instance
export const adminConfigurationService = new AdminConfigurationService()
export default adminConfigurationService
